import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import axios from 'axios';
import { powerAtom, Power } from '../atoms-types/power';
import { BuildingIDProps } from '../atoms-types/chargers';
import PowerGraph from '../charts-graphs/powerGraph';
import moment, { Moment } from 'moment-timezone';
import { Card, CardContent } from '@mui/material';
import DownloadButton from '../buttons/downloadButton';
import GraphTimePicker from '../buttons/graphTimePicker';
import useWebSocket from '../wsHook';
import { endpointLink, websocketLink } from '../../index';
import ConvertTime from '../converters/timestampConverter';

const PowerGraphHandler: React.FC<BuildingIDProps> = ({ BuildingID }) => {
  const [powerData, setPowerData] = useAtom(powerAtom);
  const [startDateTime, setStartDateTime] = useState<Moment>(moment().subtract(24, 'hour').tz('America/New_York'));
  const [endDateTime, setEndDateTime] = useState<Moment>(moment().tz('America/New_York'));
  const [selectedRange, setSelectedRange] = useState<string>("Last 24 hours");
  const { messages } = useWebSocket(websocketLink, ['power']);
  
  // Track the last processed message index to avoid reprocessing
  const lastProcessedIndexRef = useRef(-1);

  // Effect to fetch data when DateTime values change
  useEffect(() => {
    refetch();
  }, [startDateTime, endDateTime]);

  // Handle websocket messages for real-time updates - only process new messages
  useEffect(() => {
    if (messages.length === 0) return;
    
    // Only process messages we haven't seen before
    const newMessages = messages.slice(lastProcessedIndexRef.current + 1);
    lastProcessedIndexRef.current = messages.length - 1;
    
    newMessages.forEach((messageObj) => {
      const { channel, message } = messageObj;
      
      try {
        if (channel === 'power' && message && selectedRange !== "Custom") {
          const powerMessage = message as Power;
          if (powerMessage.BuildingID?.toLowerCase() === BuildingID?.toLowerCase()) {
            // Convert timestamp to UTC format to match fallback data
            const convertedMessage = {
              ...powerMessage,
              Timestamp: moment.tz(powerMessage.Timestamp, 'America/New_York').utc().format('YYYY-MM-DD HH:mm:ss')
            };
            
            setPowerData((prev) => {
              const buildingID = BuildingID.toLowerCase();
              const existingData = prev[buildingID] || [];
              
              // Check if this timestamp already exists to avoid duplicates
              const timestampExists = existingData.some(data => data.Timestamp === convertedMessage.Timestamp);
              if (timestampExists) {
                return prev;
              }
              
              // Append new data point
              const newData = [...existingData, convertedMessage];
              return {
                ...prev,
                [buildingID]: newData
              };
            });
          }
        }
      } catch (error) {
        console.error('Failed to process power message:', error);
      }
    });
  }, [messages, BuildingID, selectedRange, setPowerData]);

  // Reset message index when dependencies change
  useEffect(() => {
    lastProcessedIndexRef.current = -1;
  }, [BuildingID, selectedRange]);

  const { isFetching, isError, error, refetch } = useQuery({
    queryKey: ['powerGraph', BuildingID, startDateTime, endDateTime],
    staleTime: Infinity,
    queryFn: async () => {
      const response = await axios.get<Power[]>(`${endpointLink}powerdata-graph`, {
        params: {
          BuildingID: BuildingID,
          start: startDateTime.toISOString(),
          end: endDateTime.toISOString(),
        },
      });
      const data = response.data;
      updatePowerData(data);
      return data;
    },
  });

  function updatePowerData(data: Power[]) {
    if (!data || data.length === 0) {
      console.error("No data available to update power information");
      return;
    }

    // Filter data for the current BuildingID only
    const filteredData = data.filter(powerData => 
      powerData.BuildingID && 
      powerData.BuildingID.toLowerCase() === BuildingID?.toLowerCase()
    );

    if (filteredData.length > 0) {
      setPowerData((prev) => {
        const newState = {
          ...prev,
          [BuildingID!.toLowerCase()]: filteredData,
        };
        return newState;
      });
    }
  }

  const renderLoadingOrError = () => {
    if (isFetching) {
      return <p>Loading...</p>;
    } else if (isError) {
      return <p>Error: {error.message}</p>;
    }
    return null;
  };

  return (
    <>
      {renderLoadingOrError()}
      <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', justifyContent: 'center' }}>
        <PowerGraph 
          BuildingID={BuildingID} 
          startDateTime={startDateTime}
          endDateTime={endDateTime}
        />
        <CardContent style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <GraphTimePicker
              startDateTime={startDateTime}
              endDateTime={endDateTime}
              setStartDateTime={setStartDateTime}
              setEndDateTime={setEndDateTime}
              selectedRange={selectedRange}
              setSelectedRange={setSelectedRange}
            />
            <DownloadButton
              data={(powerData[BuildingID?.toLowerCase() || ''] || []).map((item: Power) => ({
                Timestamp: item.Timestamp,
                Grid: item.Grid,
                Load: item.Load,
                Solar: item.Solar,
                Demand: item.Demand,
                DayPeak: item.DayPeak
              }))}
              filename={`${BuildingID}_power_${startDateTime.format('YYYY-MM-DD')}_to_${endDateTime.format('YYYY-MM-DD')}`}
              timestampField="Timestamp"
              valueFields={["Grid", "Load", "Solar", "Demand", "DayPeak"]}
              disabled={!powerData[BuildingID?.toLowerCase() || '']?.length}
              timezone="America/New_York"
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export default PowerGraphHandler;



